// src/contexts/BackgroundContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const BackgroundContext = createContext();

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error('useBackground must be used within a BackgroundProvider');
  }
  return context;
};

export const BackgroundProvider = ({ children }) => {
  const [bgSettings, setBgSettings] = useState({
    type: 'solid',
    solidColor: '#292929',
    gradientColor1: '#1a1a2e',
    gradientColor2: '#16213e',
    gradientColor3: '',
    gradientDirection: '135deg',
    imageUrl: '',
    imageSize: 'cover',
    imagePosition: 'center',
    imageRepeat: 'no-repeat',
    imageAttachment: 'scroll',
    overlayEnabled: false,
    overlayColor: '#000000',
    overlayOpacity: 0.3,
    overlayBlur: 0,
    headerTextColor: '#ffffff'
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBgSettings = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/bg_settings.json`, {
          cache: "no-store"
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load bg_settings.json (${response.status})`);
        }
        
        const data = await response.json();
        setBgSettings(prev => ({
          ...prev,
          ...data
        }));
      } catch (error) {
        console.warn("Using default background settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBgSettings();
  }, []);

  const getBackgroundCSS = () => {
    const settings = bgSettings;
    
    switch (settings.type) {
      case 'solid':
        return `background-color: ${settings.solidColor || '#292929'};`;
        
      case 'gradient':
        const colors = [settings.gradientColor1, settings.gradientColor2];
        if (settings.gradientColor3 && settings.gradientColor3 !== '#000000' && settings.gradientColor3 !== '') {
          colors.push(settings.gradientColor3);
        }
        return `background: linear-gradient(${settings.gradientDirection}, ${colors.join(', ')}); background-attachment: fixed;`;
        
      case 'image':
        return `
          background-image: ${settings.imageUrl ? `url(${settings.imageUrl})` : 'none'};
          background-size: ${settings.imageSize || 'cover'};
          background-position: ${settings.imagePosition || 'center'};
          background-repeat: ${settings.imageRepeat || 'no-repeat'};
          background-attachment: ${settings.imageAttachment || 'scroll'};
        `;
        
      default:
        return `background-color: #292929;`;
    }
  };

  useEffect(() => {
    if (isLoading) return;

    const styleId = 'global-background-style';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    const settings = bgSettings;
    const bgCSS = getBackgroundCSS();
    const overlayBlur = settings.overlayEnabled ? settings.overlayBlur || 0 : 0;
    const overlayOpacity = settings.overlayEnabled ? settings.overlayOpacity || 0.3 : 0;
    const overlayColor = settings.overlayEnabled ? (settings.overlayColor || '#000000') : 'transparent';
    const headerColor = settings.headerTextColor || '#ffffff';
    
    styleElement.textContent = `
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        min-height: 100vh !important;
        width: 100% !important;
        ${bgCSS}
      }
      
      #root {
        min-height: 100vh;
        width: 100%;
        margin: 0;
        padding: 0;
        position: relative;
      }
      
      #root::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 0;
        backdrop-filter: blur(${overlayBlur}px);
        -webkit-backdrop-filter: blur(${overlayBlur}px);
        background-color: ${overlayColor};
        opacity: ${overlayOpacity};
      }
      
      /* Page header headings */
      .news-page-header h1,
      .news-page-header h2,
      .news-page-header h3,
      .news-page-header h4,
      .news-page-header h5,
      .news-page-header h6 {
        color: ${headerColor} !important;
      }
      
      /* Direct child paragraph of news-page-header (the subheading text below the title) */
      .news-page-header > p {
        color: ${headerColor} !important;
      }
      
      /* News sidebar h3 */
      .news-sidebar h3 {
        color: ${headerColor} !important;
      }
    `;

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [bgSettings, isLoading]);

  const value = {
    bgSettings,
    isLoading,
    getBackgroundCSS
  };

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
};